export const FUNCOES_ADIANTAMENTO_MDF = [
  { id: 'corte', label: 'Corte' },
  { id: 'laka-borda', label: 'Laka borda' },
  { id: 'usinagem', label: 'Usinagem' },
  { id: 'pu', label: 'PU' },
  { id: 'coladeira', label: 'Coladeira' },
  { id: 'furadeira', label: 'Furadeira' },
] as const;

export type FuncaoAdiantamentoMdf = (typeof FUNCOES_ADIANTAMENTO_MDF)[number]['id'];

export interface ExtraAdiantamentoMdf {
  id: string;
  descricao: string;
  material: string;
  funcao: FuncaoAdiantamentoMdf | '';
}

export interface EstadoProdutoAdiantamentoMdf {
  /** Peças do Tempo de Corte ocultas neste produto (não adiantam). */
  ocultos: string[];
  /** Peças extras do TC (ex.: aglomerado) incluídas neste produto. */
  incluidos: string[];
  extras: ExtraAdiantamentoMdf[];
}

export interface PecaAdiantamentoMdf {
  itemId: string;
  produtoId: string;
  produtoNome: string;
  lote: string;
  descricao: string;
  material: string;
  funcao: FuncaoAdiantamentoMdf | '';
  origem: 'tc' | 'extra';
}

export interface EstadoAdiantamentoMdf {
  funcoesPorPeca: Record<string, FuncaoAdiantamentoMdf | ''>;
  porProduto: Record<string, EstadoProdutoAdiantamentoMdf>;
  /** Produtos incluídos na lista de adiantamento (vários ao mesmo tempo). */
  produtoIdsNaLista: string[];
  /** Data de adiantamento por linha unificada (família + descrição + material). */
  datasPorLinha: Record<string, string>;
  dataInicioFiltro: string;
  salvoEm: string;
}

export function estadoAdiantamentoMdfVazio(): EstadoAdiantamentoMdf {
  return {
    funcoesPorPeca: {},
    porProduto: {},
    produtoIdsNaLista: [],
    datasPorLinha: {},
    dataInicioFiltro: '',
    salvoEm: '',
  };
}

export function estadoProdutoAdiantamentoVazio(): EstadoProdutoAdiantamentoMdf {
  return { ocultos: [], incluidos: [], extras: [] };
}

function materialNorm(material: unknown): string {
  return String(material || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase();
}

export function pecaMaterialEhMdf(material: unknown): boolean {
  return materialNorm(material).includes('MDF');
}

export function pecaMaterialEhAglomerado(material: unknown): boolean {
  const t = materialNorm(material);
  return t.includes('AGLOMERADO') || t.includes('AGLO');
}

export function pecaMaterialAdiantavel(material: unknown): boolean {
  return pecaMaterialEhMdf(material) || pecaMaterialEhAglomerado(material);
}

export function rotuloFuncaoMdf(id: string): string {
  return FUNCOES_ADIANTAMENTO_MDF.find((f) => f.id === id)?.label ?? id;
}

export function chavePecaAdiantamentoMdf(
  produtoId: string,
  item: { id?: unknown; descricao?: unknown; material?: unknown }
): string {
  const idItem = String(item.id || '').trim();
  if (idItem) return `${produtoId}::id::${idItem}`;
  const desc = String(item.descricao || '').trim().toUpperCase();
  const mat = String(item.material || '').trim().toUpperCase();
  return `${produtoId}::fp::${desc}|${mat}`;
}

export function isFuncaoAdiantamentoMdf(v: unknown): v is FuncaoAdiantamentoMdf {
  return FUNCOES_ADIANTAMENTO_MDF.some((f) => f.id === v);
}

/** Dia civil YYYY-MM-DD a partir de datetime-local / ISO. */
export function diaIsoDeDataHora(valor: unknown): string {
  const s = String(valor || '').trim();
  if (!s) return '';
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1];
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
